import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HelpTip } from '../components/portal/HelpTip';
import { toast } from 'sonner';
import { CheckCircle2, Upload } from 'lucide-react';
import api from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const d = (s) => (s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : '—');

const Period = ({ p, onDone }) => {
  const upload = async (key, f) => {
    const fd = new FormData(); fd.append('file', f);
    try { await api.post(`/api/compliance-portal/requests/${p.request_id}/items/${key}/upload`, fd); toast.success('Uploaded'); onDone(); } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
  };
  const act = (promise, msg) => promise.then(() => { toast.success(msg); onDone(); }).catch((e) => toast.error(e.response?.data?.detail || 'Action failed'));
  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4" data-testid={`bas-period-${p.period_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base md:text-lg font-semibold" data-testid={`bas-title-${p.period_id}`}>{p.title}</h2>
        <Badge variant="outline">{p.status}</Badge>
      </div>
      <p className="text-sm">Please send by <b data-testid={`bas-send-by-${p.period_id}`}>{d(p.send_by)}</b> · ATO due date <span data-testid={`bas-due-${p.period_id}`}>{d(p.ato_due_date)}</span></p>
      {p.items.map((i) => (
        <div key={i.key} className="flex items-center justify-between gap-3 text-sm border-t border-border pt-3">
          <span>{i.label} {i.client_declared ? <span className="text-muted-foreground" data-testid={`bas-declared-${p.period_id}-${i.key}`}>– Thanks — we've noted your declaration. Your accountant will confirm what else is needed.</span>
            : <span className="text-muted-foreground">({i.status === 'outstanding' ? 'Still needed' : i.status.replace('_', ' ')})</span>}</span>
          {p.request_id && <label className="underline cursor-pointer flex items-center gap-1"><Upload className="w-4 h-4" />Upload<input type="file" className="hidden" onChange={(e) => upload(i.key, e.target.files[0])} data-testid={`bas-upload-${p.period_id}-${i.key}`} /></label>}
        </div>
      ))}
      {p.documents.length > 0 && <p className="text-xs text-muted-foreground">Uploaded: {p.documents.map((x) => x.filename).join(', ')}</p>}
      {p.request_id && (
        <div className="flex flex-wrap gap-2">
          {!p.xero_declaration ? <Button size="sm" variant="outline" onClick={() => act(api.post(`/api/portal/bas/${p.period_id}/xero-declaration`), "Thanks — we've noted your declaration. Your accountant will confirm what else is needed.")} data-testid={`bas-xero-${p.period_id}`}>
            <CheckCircle2 className="w-4 h-4 mr-1" />My bookkeeping is up to date in Xero</Button>
            : <Badge data-testid={`bas-xero-done-${p.period_id}`}>Declared up to date in Xero · {d(p.xero_declaration.at)}</Badge>}
          {!p.submitted_at && <Button size="sm" onClick={() => act(api.post(`/api/compliance-portal/requests/${p.request_id}/submit`), 'Sent to your accountant')} data-testid={`bas-submit-${p.period_id}`}>Submit</Button>}
        </div>
      )}
      <Link to={`/communications?topic=bas&ref=${p.period_id}`} className="text-xs underline" data-testid={`bas-message-${p.period_id}`}>Message your accountant about this</Link>
    </div>
  );
};

export default function MyBas() {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get('/api/portal/bas').then(({ data: x }) => setData(x)).catch(() => toast.error('Could not load')), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-4 sm:p-6 lg:p-10 space-y-8" data-testid="my-bas-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-headings font-bold">BAS & IAS</h1>
        <p className="text-sm text-muted-foreground mt-2">{data?.entity_name} · <HelpTip term="BAS">What is a BAS?</HelpTip> · <HelpTip term="IAS">What is an IAS?</HelpTip></p>
      </div>
      {data && !data.periods.length && <p className="text-sm text-muted-foreground" data-testid="bas-empty">Nothing is due for this entity right now.</p>}
      <div className="space-y-4">{data?.periods.map((p) => <Period key={p.period_id} p={p} onDone={load} />)}</div>
    </div>
  );
}
